import { memo } from 'react'

import { Box, Button, Center, Group, Slider, Stack, Text } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'

import { IconArrowDown } from '@repo/common/components/Icons'
import { usdFormatter } from '@repo/common/helpers/formatters'
import type {
  AddressableSymbol,
  VaultSymbol,
} from '@repo/common/queries/products'
import { useBuyVault } from '@repo/common/queries/vaults'

import { ContentBox } from '@/components/ContentBox'
import {
  type BasePanelProps,
  classes,
  FlowButton,
  normalize_progress,
  NumberRow,
  RatioAndDisclosures,
  RewardsRowBox,
  type StepsType,
  useFlowControl,
} from '@/flows/flow_helpers'
import {
  type ExtraAuthSyncingProps,
  PanelAuthSyncing,
  useSkipAuthSyncing,
} from '@/panels/PanelAuthSyncing'
import {
  PanelCheckWalletRisk,
  useSkipWalletRisk,
} from '@/panels/PanelCheckWalletRisk'
import {
  PanelConfirmEmail,
  useSkipConfirmEmail,
} from '@/panels/PanelConfirmEmail'
import {
  type ExtraIdentificationProps,
  PanelIdentification,
  useSkipIdentification,
} from '@/panels/PanelIdentification'
import {
  PanelLocationCheck,
  useSkipLocationCheck,
} from '@/panels/PanelLocationCheck'
import {
  type ExtraPanelPermitProps,
  PanelPermit2,
  useSkipPermit2,
} from '@/panels/PanelPermit2'
import {
  PanelRegisterWallet,
  useRegisterWalletTitle,
  useSkipRegisterWallet,
} from '@/panels/PanelRegisterWallet'
import { PanelSignup, useSkipLogin } from '@/panels/PanelSignup'

import { useVaultDepositFields } from './useVaultDepositFields'

import slider_classes from './VaultDeposit.module.css'

type DepositPanelProps = BasePanelProps &
  ExtraAuthSyncingProps &
  ExtraIdentificationProps &
  ExtraPanelPermitProps & {
    symbol_in2: AddressableSymbol
  }

const steps: StepsType<DepositPanelProps> = normalize_progress([
  {
    title: 'Signup',
    Panel: PanelSignup,
    progress: 10,
    useSkipCheck: useSkipLogin,
  },
  {
    title: 'Confirm Email',
    Panel: PanelConfirmEmail,
    progress: 10,
    useSkipCheck: useSkipConfirmEmail,
  },
  {
    title: 'Location Check',
    Panel: PanelLocationCheck,
    progress: 5,
    useSkipCheck: useSkipLocationCheck,
  },
  {
    title: useRegisterWalletTitle,
    Panel: PanelRegisterWallet,
    progress: 5,
    useSkipCheck: useSkipRegisterWallet,
  },
  {
    title: 'Wallet Check',
    Panel: PanelCheckWalletRisk,
    progress: 5,
    useSkipCheck: useSkipWalletRisk,
  },
  {
    title: 'Identification',
    Panel: PanelIdentification,
    progress: 20,
    useSkipCheck: useSkipIdentification,
  },
  {
    title: 'Enable Permit2',
    Panel: PanelPermit2,
    progress: 5,
    useSkipCheck: useSkipPermit2,
  },
  {
    title: 'Enable Permit2',
    Panel: ({ symbol_in2, ...args }: DepositPanelProps) => (
      <PanelPermit2 {...args} symbol_in={symbol_in2} />
    ),
    progress: 5,
    useSkipCheck: ({ symbol_in2 }: { symbol_in2: AddressableSymbol }) =>
      useSkipPermit2({ symbol_in: symbol_in2 }),
  },
  {
    title: 'Auth Syncing',
    Panel: PanelAuthSyncing,
    progress: 5,
    useSkipCheck: useSkipAuthSyncing,
  },
])

export const VaultDeposit = memo<{
  symbol: VaultSymbol
  onCompleted: () => void
  // eslint-disable-next-line max-lines-per-function
}>(function VaultDeposit({ symbol, onCompleted }) {
  const {
    form,
    fund_symbol,
    is_loading,
    maxes,
    wallet_isConnected,
    form_valid,
    paused,
    error_reason,
    deposits,
    deposit_total_focus,
    percentage_focus,
    vault_price_per_token,
    vault_tokens,
    allowed_kyc_levels,
    level,
  } = useVaultDepositFields({ symbol })

  const deposit_query = useBuyVault({ symbol })

  const { submitted, onSubmit, modal } = useFlowControl({
    steps,
    extra_args: {
      symbol_in: 'USDC',
      symbol_in2: fund_symbol,
      allowed_kyc_levels,
      action_term: 'depositing',
      level,
    },
    final_action: () => deposit_query.mutateAsync(deposits).then(onCompleted),
  })

  const deposit_max_usdc_tokens = form.getValues().deposit_max_usdc_tokens

  const fisn_rewards = {
    percentage: 10,
    tokens: 100,
    token_value: 100 * 0.05,
  }

  return (
    <Stack maw="100%">
      {modal}
      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack
          maw="calc(100vw - 2 * var(--app-body-margin))"
          w="500px"
          gap="xxs"
        >
          <Box pos="relative">
            <Box className={classes.down_box}>
              <Center h="100%">
                <IconArrowDown
                  style={{ width: '70%', height: '70%' }}
                  stroke={1.5}
                />
              </Center>
            </Box>
            <ContentBox
              p="lg"
              gap="xs"
              mod={{
                focused:
                  deposit_total_focus.focused || percentage_focus.focused,
                error: Boolean(
                  form.getInputProps('deposit_total_usdc_tokens').error,
                ),
              }}
              data-testid="deposit_container"
            >
              <Text size="md" lh="1" opacity={0.7}>
                Deposit
              </Text>
              <NumberRow
                {...form.getInputProps('deposit_total_usdc_tokens', {
                  withError: false,
                })}
                ref={deposit_total_focus.ref}
                disabled={is_loading || submitted}
                min={0}
                max={form.getValues().deposit_max_usdc_tokens}
                prefix="$"
                placeholder="$0"
                aria-label="deposit total value"
              />
              <Stack w="100%" align="flex-end" gap="0">
                <Box
                  w="100%"
                  pt="calc(0.5 * var(--mantine-font-size-lg))"
                  pb="calc(1 * var(--mantine-font-size-lg))"
                  mb="2px"
                  px="calc(0.5 * var(--mantine-font-size-lg))"
                >
                  <Slider
                    w="100%"
                    defaultValue={50}
                    {...form.getInputProps('percentage_of_total')}
                    ref={percentage_focus.ref}
                    marks={[
                      return_mark({
                        percentage: 25,
                        deposit_max_usdc_tokens,
                        form,
                      }),
                      return_mark({
                        percentage: 50,
                        deposit_max_usdc_tokens,
                        form,
                      }),
                      return_mark({
                        percentage: 75,
                        deposit_max_usdc_tokens,
                        form,
                      }),
                      return_mark({
                        percentage: 100,
                        deposit_max_usdc_tokens,
                        form,
                      }),
                    ]}
                    label={null}
                    classNames={{
                      thumb: slider_classes.slider_thumb,
                      markLabel: slider_classes.slider_mark_label,
                    }}
                    thumbSize="var(--mantine-font-size-lg)"
                  />
                </Box>
              </Stack>
              <Text size="sm" opacity={0.7}>
                The Vault optimizes the deposit ratio in order to maximize
                returns on its liquidity position given its current undeployed
                asset balances.
              </Text>
              <NumberRow
                readonly
                value={deposits.deposit_usdc_tokens}
                symbol="USDC"
                aria-label="deposit USDC tokens"
                balance_number={maxes.balance_usdc_tokens}
              />
              <NumberRow
                readonly
                value={deposits.deposit_fund_tokens}
                symbol={fund_symbol}
                aria-label={`deposit ${fund_symbol} tokens`}
                balance_number={maxes.balance_fund_tokens}
                balance_maximumFractionDigits={4}
              />
            </ContentBox>
          </Box>
          <ContentBox p="lg" gap="xs" data-testid="receive_container">
            <Text size="md" lh="1" opacity={0.7}>
              Receive
            </Text>
            <NumberRow
              readonly
              value={vault_tokens}
              symbol={symbol}
              aria-label={`receive ${symbol} tokens`}
              balance_maximumFractionDigits={4}
            />
          </ContentBox>
          <FlowButton
            wallet_isConnected={wallet_isConnected}
            form_valid={form_valid}
            is_loading={is_loading}
            paused={paused}
            error_reason={error_reason}
            mutation_query={deposit_query}
            action_word="Deposit"
          />
          <RewardsRowBox
            message="Free Vault Rewards approximately after a week"
            {...fisn_rewards}
          />
          <RatioAndDisclosures
            symbol={fund_symbol}
            price={vault_price_per_token}
            maximumFractionDigits={6}
          >
            <Stack gap="xs" px="sm">
              <Group justify="space-between">
                <Text size="sm">TODO</Text>
              </Group>
            </Stack>
          </RatioAndDisclosures>
        </Stack>
      </form>
    </Stack>
  )
})

function return_mark<V extends { percentage_of_total: number }>({
  percentage,
  deposit_max_usdc_tokens,
  form,
}: {
  percentage: number
  deposit_max_usdc_tokens: number
  form: UseFormReturnType<V>
}) {
  return {
    value: percentage,
    label: (
      <Button
        size="compact-sm"
        variant="subtle"
        // odd TS issue
        onClick={() =>
          form.setValues({ percentage_of_total: percentage } as Partial<V>)
        }
      >
        {percentage == 100
          ? 'MAX'
          : usdFormatter((percentage / 100) * deposit_max_usdc_tokens, false)}
      </Button>
    ),
  }
}
